import { SafeArea } from 'capacitor-plugin-safe-area'
import { addScreenOrientationChangeListener } from './screenOrientation'
import { Capacitor } from '@capacitor/core'

/**
 * Safe area insets interface
 */
export interface SafeAreaInsets {
  top: number
  bottom: number
  left: number
  right: number
}

/**
 * Gets the current safe area insets directly from the plugin.
 * Returns zero values for non-native platforms.
 *
 * @returns Promise resolving to safe area insets
 * @example
 * ```typescript
 * const insets = await getSafeAreaInsets()
 * console.log('Top inset:', insets.top)
 * ```
 */
export const getSafeAreaInsets = async (): Promise<SafeAreaInsets> => {
  // Return zero values for non-native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('[SafeArea] Non-native platform, returning zero insets')
    return {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    }
  }

  try {
    console.log('[SafeArea] Fetching safe area insets from plugin...')
    const data = await SafeArea.getSafeAreaInsets()
    const { insets } = data
    console.log('[SafeArea] Retrieved insets:', insets)
    return insets as SafeAreaInsets
  } catch (error) {
    console.error('[SafeArea] Failed to get safe area insets:', error)
    // Return zero values as fallback
    return {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    }
  }
}

// Keep track of the style element
let safeAreaStyleElement: HTMLStyleElement | null = null

export const updateSafeAreaCssVariables = async () => {
  try {
    console.log('[SafeArea] Updating CSS variables...')
    const insets = await getSafeAreaInsets()
    console.log('[SafeArea] Using insets for CSS variables:', insets)

    // Create or update the style element
    if (!safeAreaStyleElement) {
      safeAreaStyleElement = document.createElement('style')
      safeAreaStyleElement.id = 'safe-area-variables'
      document.head.appendChild(safeAreaStyleElement)
      console.log('[SafeArea] Created style element for safe area variables')
    }

    // Generate CSS variables for root and ion-modal elements
    const cssVariables = Object.entries(insets)
      .map(([key, value]) => `--ion-safe-area-${key}: ${value}px !important`)
      .join(';\n  ')

    const cssContent = `
:root,
ion-modal {
  ${cssVariables};
}
`

    safeAreaStyleElement.textContent = cssContent
    console.log(
      '[SafeArea] Updated style element with CSS variables:',
      cssVariables,
    )
    console.log('[SafeArea] CSS variables updated successfully')
  } catch (error) {
    console.error('[SafeArea] Failed to update CSS variables:', error)
  }
}

export const initSafeArea = () => {
  // Initial safe area setup
  console.log('[SafeArea] Initializing safe area on app startup...')
  void updateSafeAreaCssVariables()

  // Listen for safe area changes
  console.log('[SafeArea] Setting up safe area change listener...')
  void SafeArea.addListener('safeAreaChanged', () => {
    console.log('[SafeArea] Safe area changed event triggered')
    void updateSafeAreaCssVariables()
  }).catch()

  // Listen for screen orientation changes and update safe area
  console.log('[SafeArea] Setting up screen orientation change listener...')
  void addScreenOrientationChangeListener(() => {
    console.log('[SafeArea] Screen orientation changed, updating safe area...')
    void updateSafeAreaCssVariables()
  }).catch()
}
